import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  ThemeProvider,
  MessageBar,
  MessageBarType,
  MessageBarButton,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { useFileBrowserStore, useOfficeTheme } from '@/hooks';
import { connectionState } from '@/services/connectionState';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { Toolbar } from '@/components/Toolbar';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { FileList } from '@/components/FileList';
import { PreviewPanel } from '@/components/PreviewPanel';
import { UploadProgress } from '@/components/UploadProgress';
import { CreateFolderDialog } from '@/components/CreateFolderDialog';
import { MoveDialog } from '@/components/MoveDialog';
import { RenameDialog } from '@/components/RenameDialog';
import { LoginScreen } from '@/components/LoginScreen';
import { DropZone } from '@/components/DropZone';
import { SiteHomeView } from '@/components/SiteHomeView';
import { SaveBar } from '@/components/SaveBar';
import { EnvBanner } from '@/components/EnvBanner';
import { logger } from '@/utils/logger';
import { lightTheme, darkTheme } from '@/styles/themes';

export function App() {
  const {
    auth,
    error,
    setError,
    notification,
    dismissNotification,
    initAuth,
    previewItem,
    uploads,
    currentSiteId,
    silentRefresh,
    loadSites,
  } = useFileBrowserStore();
  const themeMode = useOfficeTheme();
  const connectionStale = useSyncExternalStore(connectionState.subscribe, connectionState.isStale);
  useKeyboardShortcuts();
  const [appError, setAppError] = useState<string | null>(null);

  // Apply theme class to body
  useEffect(() => {
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${themeMode}-mode`);
  }, [themeMode]);

  useEffect(() => {
    try {
      initAuth();
    } catch (e) {
      logger.error('App initialization error:', e);
      setAppError(e instanceof Error ? e.message : 'Initialization error');
    }
  }, [initAuth]);

  // Auto-refresh: pick up files added by other people/tools (direct SharePoint
  // uploads, Teams, OneDrive sync, the migration) without a manual Refresh.
  // Backendless (no webhooks), so: refresh on focus/visibility + a gentle poll
  // while the pane is visible. silentRefresh is a no-op unless browsing a site,
  // and only updates the list when it actually changed.
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const POLL_MS = 45000;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') silentRefresh();
    };
    const interval = setInterval(refreshIfVisible, POLL_MS);
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, [auth.isAuthenticated, silentRefresh]);

  const theme = themeMode === 'dark' ? darkTheme : lightTheme;

  // Show app-level errors
  if (appError) {
    return (
      <ThemeProvider theme={theme}>
        <EnvBanner />
        <div className="app-container">
          <MessageBar messageBarType={MessageBarType.error}>Error: {appError}</MessageBar>
        </div>
      </ThemeProvider>
    );
  }

  // Loading state
  if (auth.isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <EnvBanner />
        <div className="app-container loading">
          <Spinner size={SpinnerSize.large} label="Loading" />
        </div>
      </ThemeProvider>
    );
  }

  // Not authenticated
  if (!auth.isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <EnvBanner />
        <LoginScreen />
      </ThemeProvider>
    );
  }

  // Main app
  return (
    <ThemeProvider theme={theme}>
      <div className="app-container">
        <EnvBanner />
        {connectionStale && (
          <MessageBar
            messageBarType={MessageBarType.severeWarning}
            actions={
              <MessageBarButton onClick={() => (currentSiteId ? silentRefresh() : loadSites(true))}>
                Reconnect
              </MessageBarButton>
            }
          >
            Your session expired and the connection was lost. What you see may be out of date.
          </MessageBar>
        )}
        {/* Action feedback (success auto-dismisses) and load errors */}
        {(notification || error) && (
          <MessageBar
            messageBarType={
              notification?.type === 'success' ? MessageBarType.success : MessageBarType.error
            }
            onDismiss={() => (notification ? dismissNotification() : setError(null))}
            dismissButtonAriaLabel="Dismiss"
          >
            {notification?.text || error}
          </MessageBar>
        )}

        {/* No site selected -> the home / site picker. Otherwise the browser. */}
        {!currentSiteId ? (
          <SiteHomeView />
        ) : (
          <>
            {/* Mobile email save mode: persistent bar while the user browses to
                a destination folder (no-op/hidden on desktop). */}
            <SaveBar />

            {/* Breadcrumbs first, then toolbar with search */}
            <Breadcrumbs />
            <Toolbar />

            {/* DropZone wraps ONLY the list/grid area so the drop overlay covers the
                content region and not the breadcrumbs / toolbar / search bar. */}
            <DropZone>
              <div className="main-content">
                <FileList />
                {previewItem && <PreviewPanel />}
              </div>
            </DropZone>

            {uploads.length > 0 && <UploadProgress />}
          </>
        )}

        <CreateFolderDialog />
        <MoveDialog />
        <RenameDialog />
      </div>
    </ThemeProvider>
  );
}
